import { useAuth } from './useAuth';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useCheckSubscription, PLAN_LIMITS, type SubscriptionPlan, type SubscriptionData } from '@/services/billing';
import { useEntitlements } from './useEntitlements';
import { syncStorage } from '@/lib/storage';
import { debug } from '@/lib/debug';

export type { SubscriptionPlan } from '@/services/billing';

// PLANS-1 — mirrors the shape of PLAN_LIMITS.orbit (the fullest paid
// tier baseline outside Business). Kept structural rather than pulling
// `typeof PLAN_LIMITS['orbit']` so consumers that spread limits into
// their own view state don't accidentally lock in the readonly
// modifiers.
type PlanLimits = {
  projects: number;
  teamMembers: number;
  clientCap: number;
  projectCap: number;
  storage: string;
  storageGB: number;
  novaAiActionsPerMonth: number;
  premiumThemes: boolean;
  customBranding: boolean;
  customDomain: boolean;
  advancedVideoProofing: boolean;
  automation: boolean;
  portalCustomization: boolean;
  apiAccess: boolean;
  prioritySupport: boolean;
  creatorAccounts: boolean;
  teamManagement: boolean;
  deliveryGalleries: boolean;
  workflowTemplates: boolean;
  emailTemplates: boolean;
  analytics: boolean;
  aiFeatures: boolean;
  advancedAutomation: boolean;
  advancedAnalytics: boolean;
  productionManagement: boolean;
};

interface SubscriptionState {
  plan: SubscriptionPlan;
  isPaid: boolean;              // any paid plan
  isStarter: boolean;           // orbit_starter (entry paid)
  isOrbit: boolean;             // orbit (middle paid)
  isBusiness: boolean;          // orbit_business
  /** True on Orbit or higher — parity with the pre-PLANS-1 `isPro` name. */
  isPro: boolean;
  isFree: boolean;
  isTrial: boolean;
  trialEnd: string | null;
  isLoading: boolean;
  subscriptionEnd: string | null;
  billingInterval: 'monthly' | 'yearly' | null;
  limits: PlanLimits;
  refetch: () => Promise<unknown>;
  // Feature access helpers
  canAccessAutomation: boolean;
  canAccessCreatorAccounts: boolean;
  canAccessTeamManagement: boolean;
  canAccessPremiumThemes: boolean;
  // Entitlement overrides
  hasFullAccess: boolean;
  hasBetaAccess: boolean;
  hasAIAccess: boolean;
  hasPrerelease: boolean;
  hasDemoModeAccess: boolean;
  grantedTier: SubscriptionPlan | null;
  entitlementsLoading: boolean;
  /**
   * PAYWALL-1 — the SERVER's answer to "does this org have a plan",
   * with nothing client-side lifting it. `isPaid` folds in the demo
   * overlay (`isDemo` / `demo_mode`), which is presentation-only and
   * must never open the account. Anything that decides whether the
   * paywall is satisfied reads THIS, not `isPaid`.
   */
  hasServerPlan: boolean;
  /** PAYWALL-1 — `check-subscription` failed; `hasServerPlan` is unknown. */
  subscriptionUnavailable: boolean;
}

export const useSubscription = (): SubscriptionState => {
  const { user } = useAuth();
  const { isDemo } = useDemoMode();
  const { data, isLoading: subscriptionLoading, refetch } = useCheckSubscription();
  const { entitlements, isLoading: entitlementsLoading } = useEntitlements();

  // Get the actual plan from Stripe or fallback to free
  const actualPlan = data?.plan || 'free';
  const isTrial = data?.is_trial || false;
  const subscriptionUnavailable = Boolean(data?.unavailable);
  // Server truth only: `subscribed` is `effective_plan_for_org !== 'free'`
  // (subscription rows + tier entitlements, resolved in Postgres).
  const hasServerPlan = Boolean(data?.subscribed) || isTrial;
  const trialEnd = data?.trial_end || null;

  // PLANS-6 — resolve the effective plan the SAME way the server does
  // (migration 20260809230000_plans6_entitlement_parity.sql):
  // HIGHEST RANK WINS across the subscription and every tier-conferring
  // entitlement. Previously each entitlement branch REPLACED
  // `actualPlan`, which meant a studio paying for Orbit Business that
  // also carried a stale `grant_orbit_starter_tier` row was shown
  // Orbit Starter. A grant can lift an account; it must never downgrade
  // a paying one.
  //
  // Rank order matches PLANS-1 / effective_plan_for_org:
  //   3 orbit_business · 2 orbit · 1 orbit_starter · 0 free
  //
  // PLANS-1: `grant_orbit_pro_tier` is gone. If the DB still carries
  // legacy `grant_orbit_pro_tier` rows before the PLANS-1A migration
  // runs, they map safely down to Orbit here so studios don't lose
  // access mid-migration.
  const PLAN_BY_RANK: Record<number, SubscriptionPlan> = {
    3: 'orbit_business',
    2: 'orbit',
    1: 'orbit_starter',
    0: 'free',
  };
  const rankOf = (plan: SubscriptionPlan | string): number =>
    plan === 'orbit_business' ? 3 : plan === 'orbit' ? 2 : plan === 'orbit_starter' ? 1 : 0;

  // Entitlement-granted tier. Server-honored as of PLANS-6.
  const grantRank = entitlements.full_access || entitlements.grant_orbit_business_tier
    ? 3
    : entitlements.grant_orbit_tier || entitlements.free_tier_override
    ? 2
    : entitlements.grant_orbit_starter_tier
    ? 1
    : 0;

  // Demo / Preview Studio. PRESENTATION ONLY — deliberately NOT honored
  // by effective_plan_for_org, because `isDemo` is per-session
  // (profiles.demo_enabled + localStorage) and can't be resolved
  // org-scoped, and because the resolver also picks the Stripe Connect
  // application fee. Demo content is a seeded overlay that never
  // consumes real storage/Nova/seats, so it doesn't need real caps. An
  // account that needs real server headroom gets `full_access` or
  // `grant_orbit_business_tier` instead.
  const demoRank = entitlements.demo_mode || isDemo ? 3 : 0;

  const subRank = rankOf(actualPlan);
  const effectivePlan: SubscriptionPlan =
    PLAN_BY_RANK[Math.max(subRank, grantRank, demoRank)];

  if (grantRank > subRank) {
    debug.log('[Subscription] Plan lifted by entitlement', {
      actualPlan,
      effectivePlan,
    });
  } else if (demoRank > Math.max(subRank, grantRank)) {
    debug.log('[Subscription] Plan lifted by demo mode (client-side only)');
  }

  // Get limits for current plan
  const limits = (PLAN_LIMITS[effectivePlan] ||
    PLAN_LIMITS.free) as unknown as PlanLimits;

  const isPaid = effectivePlan !== 'free';
  const isStarter = effectivePlan === 'orbit_starter';
  const isOrbit = effectivePlan === 'orbit';
  const isBusiness = effectivePlan === 'orbit_business';
  const isPro = effectivePlan === 'orbit' || effectivePlan === 'orbit_business';
  const isFree = effectivePlan === 'free';

  // Determine if plan was granted via entitlement (not paid). Reports
  // what the grant itself confers, independent of whatever the studio
  // is separately paying for — derived from the same rank so the two
  // can't drift.
  const grantedTier: SubscriptionPlan | null =
    grantRank > 0 ? PLAN_BY_RANK[grantRank] : null;

  return {
    plan: effectivePlan,
    isPaid,
    isStarter,
    isOrbit,
    isBusiness,
    isPro,
    isFree,
    isTrial,
    trialEnd,
    isLoading: (subscriptionLoading || entitlementsLoading) && !!user,
    subscriptionEnd: data?.subscription_end || null,
    billingInterval: data?.billing_interval || null,
    limits,
    refetch,
    // Feature access helpers
    canAccessAutomation: limits.automation,
    canAccessCreatorAccounts: limits.creatorAccounts,
    canAccessTeamManagement: limits.teamManagement,
    canAccessPremiumThemes: limits.premiumThemes,
    // Entitlement overrides (cast to boolean for type safety)
    hasFullAccess: Boolean(entitlements.full_access),
    hasBetaAccess: Boolean(entitlements.beta_access),
    hasAIAccess: Boolean(entitlements.ai_assistant_access),
    hasPrerelease: Boolean(entitlements.prerelease_features),
    hasDemoModeAccess: Boolean(entitlements.demo_mode),
    grantedTier,
    entitlementsLoading,
    hasServerPlan,
    subscriptionUnavailable,
  };
};

// Helper to set plan (for demo/testing purposes only)
export const setDemoPlan = (plan: SubscriptionPlan) => {
  syncStorage.set('orbit-subscription-plan', plan);
  window.dispatchEvent(new Event('storage'));
};
